import { NextPage } from "next";
import styles from "../../styles/Gallery.module.scss";
import Image from "next/image";
import clsx from "clsx";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWindowSize } from "../../utils/hooks";
import Spacer from "../../components/Spacer";
import { GalleryItem } from "../../components/GalleryItem";
import Link from "next/link";
import { Header, HeaderVariant } from "../../components/Header";
import { collection, getDocs, query, orderBy } from "@firebase/firestore";
import { db } from "../../utils/firebase";
import { CuratedItem } from "../../utils/types";
import { Loading } from "../../components/Loading";
import { Meta } from "../../components/Meta";
import { MediaQueryContext } from "../../context/mediaQuery/context";

const Gallery: NextPage = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const {
    state: { tablet },
  } = useContext(MediaQueryContext);
  const pRef = useRef<HTMLParagraphElement>(null);
  const windowSize = useWindowSize();
  const getTextBoxHeight = useMemo(() => {
    if (!pRef.current) {
      return tablet ? 732 : 1031;
    }

    // p + distance from top + distance from bottom
    return pRef.current.clientHeight + (tablet ? 132 + 102 : 218 + 170);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowSize, tablet]);

  const [loading, setLoading] = useState(true);

  const [curatedItems, setCuratedItems] = useState<CuratedItem[]>([]);
  useEffect(() => {
    getDocs(query(collection(db, "curated-collections"), orderBy("order")))
      .then((querySnapshot) => {
        setCuratedItems(
          querySnapshot.docs.map(
            (doc) =>
              ({
                documentId: doc.id,
                ...doc.data(),
              } as CuratedItem)
          )
        );
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className={"relative"}>
      <Meta title={t("gallery.text")} />

      <main className={clsx(styles.main, "column ai-center")}>
        <Header variant={HeaderVariant.light} />
        <Spacer height={40} className={"hideOnTablet"} />
        <Spacer height={24} className={"hideOnDesktop"} />
        <div
          className={clsx(styles.textContainer, {
            [styles.textContainerExpanded]: expanded,
          })}
          style={{ ...(expanded && { height: getTextBoxHeight }) }}
        >
          <Spacer height={64} className={"hideOnTablet"} />
          <Spacer height={40} className={"hideOnDesktop"} />
          <div className={styles.tagline1}>{t("gallery.tagline1")}</div>
          <Spacer height={24} className={"hideOnTablet"} />
          <Spacer height={16} className={"hideOnDesktop"} />
          <div className={styles.tagline2}>{t("gallery.tagline2")}</div>
          <Spacer height={46} className={"hideOnTablet"} />
          <Spacer height={27} className={"hideOnDesktop"} />
          <p
            ref={pRef}
            dangerouslySetInnerHTML={{
              __html: t("gallery.p1"),
            }}
          />
          <div
            className={clsx(styles.arrow, { [styles.arrowExpanded]: expanded })}
            onClick={() => setExpanded(!expanded)}
          >
            <Image
              src={"/arrow-3.svg"}
              width={tablet ? 24 : 42}
              height={tablet ? 24 : 42}
              layout={"fixed"}
              alt={"arrow icon"}
            />
          </div>
        </div>

        {loading && (
          <>
            <Spacer height={80} />
            <div
              className={"column ai-center jc-center"}
              style={{ height: 200 }}
            >
              <Loading />
            </div>
          </>
        )}
        {curatedItems.map((item) => (
          <GalleryItem data={item} key={item.documentId} />
        ))}

        <Spacer height={100} />
        <Link href={"/gallery/all"}>
          <a className={"outlineButton"}>{t("gallery.viewAllXNFTs")}</a>
        </Link>
        <Spacer height={200} />
      </main>
    </div>
  );
};

export default Gallery;
